/**
 * Dahua Lite 4000 (4KS2 / 4KS3) and WizSense 4000-EI NVR catalog —
 * exhaustive sweep of every SKU with a primary Dahua datasheet PDF on
 * materialfile.dahuasecurity.com (cpq datasheetzipfiles tree).
 *
 * Every entry below is verified against a primary Dahua datasheet PDF
 * fetched on 2026-05-14. The exact source URL appears as a comment above
 * each entry. Fields are left undefined when the datasheet doesn't list a
 * value — never guessed.
 *
 * Series notes (from the datasheets themselves):
 *   - Lite 4KS2 (2022 firmware, "L" datasheet rev): SMD Plus by recorder
 *     only; AI by Camera passthrough. HS models have 10/100 Mbps NIC,
 *     1 SATA bay (10 TB max per disk). 1U 2-HDD models bump NIC to GbE,
 *     2 SATA (10 TB each). Bandwidth: Access 80/Storage 80/Forward 60
 *     for HS, Access 160/Storage 160/Forward 64 for 1U 2-HDD.
 *   - Lite 4KS3 (2023-2024 firmware, "S0" datasheet rev): IP cameras up
 *     to 12 MP, HDD max 20 TB/bay. HS no-PoE models keep 10/100 NIC and
 *     80/80/60 bandwidth; HS-PoE and 1U 2-HDD bump to GbE and 160/160/80
 *     (AI-disabled). NVR4232-4KS3 is 160/128/60 (recording cap is lower).
 *   - WizSense 4000-EI (2023-2024, "S0" datasheet rev): full AI by NVR
 *     (face / perimeter / SMD Plus); IP cameras up to 16 MP, HDD max
 *     16 TB/bay. 4-ch and 8-ch HS no-PoE models cap bandwidth at 80 Mbps
 *     forwarding; everything else (16-ch HS, 1U 2-HDD, 1.5U 4-HDD) is
 *     256 Mbps AI-disabled (180 Mbps AI-enabled).
 *
 * SKUs not in this file: NVR4232-4KS2 (never released — Dahua skipped 32-ch
 * on 4KS2 line, only appears starting 4KS3); NVR4104HS-4P-EI and
 * NVR4116HS-4P-EI (no datasheet PDF on materialfile CDN as of 2026-05-14;
 * the only 4-PoE EI compact model is NVR4108HS-P-EI which uses the legacy
 * "-P" suffix Dahua reserves for 4 PoE ports on 8-ch HS chassis).
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const DAHUA_NVR_4000: EquipmentCatalogEntry[] = [
  // ═════════════════════════════════════════════════════════════════════
  // Lite 4000 — 4KS2 Series (older; 8 MP max, 10 TB HDD, no AI by NVR)
  // ═════════════════════════════════════════════════════════════════════

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104HS-4KS2_L_datasheet_20220315.pdf
  {
    id: 'dahua-nvr4104hs-4ks2',
    manufacturer: 'Dahua',
    model: 'NVR4104HS-4KS2',
    category: 'nvr-recorder',
    tagline: '4-ch compact Lite 4KS2 NVR — no PoE, 1 SATA (10 TB), 8 MP, 80/60 Mbps',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104HS-4KS2_L_datasheet_20220315.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104HS-P-4KS2_L_datasheet_20220315.pdf
  {
    id: 'dahua-nvr4104hs-p-4ks2',
    manufacturer: 'Dahua',
    model: 'NVR4104HS-P-4KS2',
    category: 'nvr-recorder',
    tagline: '4-ch compact Lite 4KS2 NVR — 4-port PoE (36 W), 1 SATA (10 TB), 8 MP',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104HS-P-4KS2_L_datasheet_20220315.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 4,
      poeBudgetW: 36,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-4KS2_L_datasheet_20220315.pdf
  {
    id: 'dahua-nvr4108hs-4ks2',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-4KS2',
    category: 'nvr-recorder',
    tagline: '8-ch compact Lite 4KS2 NVR — no PoE, 1 SATA (10 TB), 8 MP, 80/60 Mbps',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-4KS2_L_datasheet_20220315.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-P-4KS2_L_datasheet_20220315.pdf
  {
    id: 'dahua-nvr4108hs-p-4ks2',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-P-4KS2',
    category: 'nvr-recorder',
    tagline: '8-ch compact Lite 4KS2 NVR — 4-port PoE (36 W), 1 SATA (10 TB), 8 MP',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-P-4KS2_L_datasheet_20220315.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 4,
      poeBudgetW: 36,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-8P-4KS2_L_datasheet_20220315.pdf
  {
    id: 'dahua-nvr4108hs-8p-4ks2',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-8P-4KS2',
    category: 'nvr-recorder',
    tagline: '8-ch compact Lite 4KS2 NVR — 8-port PoE (72 W), 1 SATA (10 TB), 8 MP',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-8P-4KS2_L_datasheet_20220315.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 8,
      poeBudgetW: 72,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-4KS2_L_datasheet_20220315.pdf
  {
    id: 'dahua-nvr4116hs-4ks2',
    manufacturer: 'Dahua',
    model: 'NVR4116HS-4KS2',
    category: 'nvr-recorder',
    tagline: '16-ch compact Lite 4KS2 NVR — no PoE, 1 SATA (10 TB), 8 MP, 80/60 Mbps',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-4KS2_L_datasheet_20220315.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-8P-4KS2_L_datasheet_20220315.pdf
  {
    id: 'dahua-nvr4116hs-8p-4ks2',
    manufacturer: 'Dahua',
    model: 'NVR4116HS-8P-4KS2',
    category: 'nvr-recorder',
    tagline: '16-ch compact Lite 4KS2 NVR — 8-port PoE (72 W), 1 SATA (10 TB), 8 MP',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-8P-4KS2_L_datasheet_20220315.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '8 MP',
      hddBays: 1,
      hddBayMaxTb: 10,
      poeInPorts: 8,
      poeBudgetW: 72,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4204-4KS2_L_datasheet_20220315.pdf
  {
    id: 'dahua-nvr4204-4ks2',
    manufacturer: 'Dahua',
    model: 'NVR4204-4KS2',
    category: 'nvr-recorder',
    tagline: '4-ch Lite 4KS2 1U NVR — no PoE, 2 SATA (10 TB each), 8 MP, 160/64 Mbps',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4204-4KS2_L_datasheet_20220315.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 64,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-4KS2_L_datasheet_20220315.pdf
  {
    id: 'dahua-nvr4208-4ks2',
    manufacturer: 'Dahua',
    model: 'NVR4208-4KS2',
    category: 'nvr-recorder',
    tagline: '8-ch Lite 4KS2 1U NVR — no PoE, 2 SATA (10 TB each), 8 MP, 160/64 Mbps',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-4KS2_L_datasheet_20220315.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 64,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-8P-4KS2_L_datasheet_20220315.pdf
  {
    id: 'dahua-nvr4208-8p-4ks2',
    manufacturer: 'Dahua',
    model: 'NVR4208-8P-4KS2',
    category: 'nvr-recorder',
    tagline: '8-ch Lite 4KS2 1U NVR — 8-port PoE (117 W), 2 SATA (10 TB each), 8 MP',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-8P-4KS2_L_datasheet_20220315.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 64,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 8,
      poeBudgetW: 117,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-4KS2_L_datasheet_20220315.pdf
  {
    id: 'dahua-nvr4216-4ks2',
    manufacturer: 'Dahua',
    model: 'NVR4216-4KS2',
    category: 'nvr-recorder',
    tagline: '16-ch Lite 4KS2 1U NVR — no PoE, 2 SATA (10 TB each), 8 MP, 160/64 Mbps',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-4KS2_L_datasheet_20220315.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 64,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-16P-4KS2_L_datasheet_20220315.pdf
  {
    id: 'dahua-nvr4216-16p-4ks2',
    manufacturer: 'Dahua',
    model: 'NVR4216-16P-4KS2',
    category: 'nvr-recorder',
    tagline: '16-ch Lite 4KS2 1U NVR — 16-port PoE (117 W), 2 SATA (10 TB each), 8 MP',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-16P-4KS2_L_datasheet_20220315.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 64,
      maxResolution: '8 MP',
      hddBays: 2,
      hddBayMaxTb: 10,
      poeInPorts: 16,
      poeBudgetW: 117,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // Lite 4000 — 4KS3 Series (12 MP IP, 20 TB HDD, GbE on PoE/2-HDD models)
  // ═════════════════════════════════════════════════════════════════════

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104HS-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4104hs-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4104HS-4KS3',
    category: 'nvr-recorder',
    tagline: '4-ch compact Lite 4KS3 NVR — no PoE, 1 SATA (20 TB), 12 MP, 80/60 Mbps',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104HS-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104HS-P-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4104hs-p-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4104HS-P-4KS3',
    category: 'nvr-recorder',
    tagline: '4-ch compact Lite 4KS3 NVR — 4-port PoE (36 W), 1 SATA (20 TB), 12 MP',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104HS-P-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 4,
      poeBudgetW: 36,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4108hs-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-4KS3',
    category: 'nvr-recorder',
    tagline: '8-ch compact Lite 4KS3 NVR — no PoE, 1 SATA (20 TB), 12 MP, 80/60 Mbps',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-P-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4108hs-p-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-P-4KS3',
    category: 'nvr-recorder',
    tagline: '8-ch compact Lite 4KS3 NVR — 4-port PoE (36 W), 1 SATA (20 TB), 12 MP',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-P-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 60,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 4,
      poeBudgetW: 36,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-8P-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4108hs-8p-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-8P-4KS3',
    category: 'nvr-recorder',
    tagline: '8-ch compact Lite 4KS3 NVR — 8-port PoE (72 W), 1 SATA (20 TB), 12 MP, GbE',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-8P-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 8,
      poeBudgetW: 72,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4116hs-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4116HS-4KS3',
    category: 'nvr-recorder',
    tagline: '16-ch compact Lite 4KS3 NVR — no PoE, 1 SATA (20 TB), 12 MP, 160/80 Mbps GbE',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-8P-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4116hs-8p-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4116HS-8P-4KS3',
    category: 'nvr-recorder',
    tagline: '16-ch compact Lite 4KS3 NVR — 8-port PoE (72 W), 1 SATA (20 TB), 12 MP, GbE',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-8P-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 1,
      hddBayMaxTb: 20,
      poeInPorts: 8,
      poeBudgetW: 72,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4204-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4204-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4204-4KS3',
    category: 'nvr-recorder',
    tagline: '4-ch Lite 4KS3 1U NVR — no PoE, 2 SATA (20 TB each), 12 MP, 160/80 Mbps',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4204-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4208-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4208-4KS3',
    category: 'nvr-recorder',
    tagline: '8-ch Lite 4KS3 1U NVR — no PoE, 2 SATA (20 TB each), 12 MP, 160/80 Mbps',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-8P-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4208-8p-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4208-8P-4KS3',
    category: 'nvr-recorder',
    tagline: '8-ch Lite 4KS3 1U NVR — 8-port PoE (130 W), 2 SATA (20 TB each), 12 MP',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-8P-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 8,
      poeBudgetW: 130,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4216-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4216-4KS3',
    category: 'nvr-recorder',
    tagline: '16-ch Lite 4KS3 1U NVR — no PoE, 2 SATA (20 TB each), 12 MP, 160/80 Mbps',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-16P-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4216-16p-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4216-16P-4KS3',
    category: 'nvr-recorder',
    tagline: '16-ch Lite 4KS3 1U NVR — 16-port PoE (130 W), 2 SATA (20 TB each), 12 MP',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-16P-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 80,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 16,
      poeBudgetW: 130,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4232-4KS3_S0_datasheet_20241115.pdf
  {
    id: 'dahua-nvr4232-4ks3',
    manufacturer: 'Dahua',
    model: 'NVR4232-4KS3',
    category: 'nvr-recorder',
    tagline: '32-ch Lite 4KS3 1U NVR — no PoE, 2 SATA (20 TB each), 12 MP, 160/60 Mbps',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4232-4KS3_S0_datasheet_20241115.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 160,
      outputBandwidthMbps: 60,
      maxResolution: '12 MP',
      hddBays: 2,
      hddBayMaxTb: 20,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // WizSense 4000-EI Series (AI by NVR: face / perimeter / SMD Plus;
  // 16 MP IP, 16 TB HDD)
  // ═════════════════════════════════════════════════════════════════════

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104HS-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4104hs-ei',
    manufacturer: 'Dahua',
    model: 'NVR4104HS-EI',
    category: 'nvr-recorder',
    tagline: '4-ch compact WizSense NVR — no PoE, 1 SATA (16 TB), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4104HS-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '16 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4108hs-ei',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-EI',
    category: 'nvr-recorder',
    tagline: '8-ch compact WizSense NVR — no PoE, 1 SATA (16 TB), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '16 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-P-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4108hs-p-ei',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-P-EI',
    category: 'nvr-recorder',
    tagline: '8-ch compact WizSense NVR — 4-port PoE (36 W), 1 SATA (16 TB), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-P-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 80,
      outputBandwidthMbps: 80,
      maxResolution: '16 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 4,
      poeBudgetW: 36,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-8P-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4108hs-8p-ei',
    manufacturer: 'Dahua',
    model: 'NVR4108HS-8P-EI',
    category: 'nvr-recorder',
    tagline: '8-ch compact WizSense NVR — 8-port PoE (72 W), 1 SATA (16 TB), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4108HS-8P-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 8,
      poeBudgetW: 72,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4116hs-ei',
    manufacturer: 'Dahua',
    model: 'NVR4116HS-EI',
    category: 'nvr-recorder',
    tagline: '16-ch compact WizSense NVR — no PoE, 1 SATA (16 TB), 16 MP, 256 Mbps GbE',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-8P-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4116hs-8p-ei',
    manufacturer: 'Dahua',
    model: 'NVR4116HS-8P-EI',
    category: 'nvr-recorder',
    tagline: '16-ch compact WizSense NVR — 8-port PoE (72 W), 1 SATA (16 TB), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4116HS-8P-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 1,
      hddBayMaxTb: 16,
      poeInPorts: 8,
      poeBudgetW: 72,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: 'Compact 1U',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4204-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4204-ei',
    manufacturer: 'Dahua',
    model: 'NVR4204-EI',
    category: 'nvr-recorder',
    tagline: '4-ch WizSense 1U NVR — no PoE, 2 SATA (16 TB each), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4204-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 4,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4208-ei',
    manufacturer: 'Dahua',
    model: 'NVR4208-EI',
    category: 'nvr-recorder',
    tagline: '8-ch WizSense 1U NVR — no PoE, 2 SATA (16 TB each), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-8P-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4208-8p-ei',
    manufacturer: 'Dahua',
    model: 'NVR4208-8P-EI',
    category: 'nvr-recorder',
    tagline: '8-ch WizSense 1U NVR — 8-port PoE (130 W), 2 SATA (16 TB each), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4208-8P-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 8,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      poeInPorts: 8,
      poeBudgetW: 130,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4216-ei',
    manufacturer: 'Dahua',
    model: 'NVR4216-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense 1U NVR — no PoE, 2 SATA (16 TB each), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-16P-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4216-16p-ei',
    manufacturer: 'Dahua',
    model: 'NVR4216-16P-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense 1U NVR — 16-port PoE (130 W), 2 SATA (16 TB each), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4216-16P-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      poeInPorts: 16,
      poeBudgetW: 130,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4232-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4232-ei',
    manufacturer: 'Dahua',
    model: 'NVR4232-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 1U NVR — no PoE, 2 SATA (16 TB each), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4232-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 2,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1U',
      alarmIO: '4 in / 2 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // ═════════════════════════════════════════════════════════════════════
  // WizSense 4000-EI 1.5U — 4-HDD chassis, dual GbE NIC on no-PoE models
  // ═════════════════════════════════════════════════════════════════════

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4416-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4416-ei',
    manufacturer: 'Dahua',
    model: 'NVR4416-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense 1.5U NVR — 4 SATA (16 TB each), 16 MP, dual GbE, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4416-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 4,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1.5U',
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4416-16P-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4416-16p-ei',
    manufacturer: 'Dahua',
    model: 'NVR4416-16P-EI',
    category: 'nvr-recorder',
    tagline: '16-ch WizSense 1.5U NVR — 16-port PoE (150 W), 4 SATA (16 TB each), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4416-16P-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 16,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 4,
      hddBayMaxTb: 16,
      poeInPorts: 16,
      poeBudgetW: 150,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1.5U',
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4432-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4432-ei',
    manufacturer: 'Dahua',
    model: 'NVR4432-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 1.5U NVR — 4 SATA (16 TB each), 16 MP, dual GbE, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4432-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 4,
      hddBayMaxTb: 16,
      poeInPorts: 0,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1.5U',
      alarmIO: '16 in / 4 out',
      networkPorts: '2× 10/100/1000 RJ-45',
    },
  },

  // https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4432-16P-EI_S0_datasheet_20240531.pdf
  {
    id: 'dahua-nvr4432-16p-ei',
    manufacturer: 'Dahua',
    model: 'NVR4432-16P-EI',
    category: 'nvr-recorder',
    tagline: '32-ch WizSense 1.5U NVR — 16-port PoE (150 W), 4 SATA (16 TB each), 16 MP, AI by NVR',
    verified: true,
    datasheetUrl: 'https://materialfile.dahuasecurity.com/uploads/cpq/prm-os-srv-res/smart/datasheetzipfiles/NVR4432-16P-EI_S0_datasheet_20240531.pdf',
    specs: {
      channels: 32,
      inputBandwidthMbps: 256,
      outputBandwidthMbps: 256,
      maxResolution: '16 MP',
      hddBays: 4,
      hddBayMaxTb: 16,
      poeInPorts: 16,
      poeBudgetW: 150,
      codecs: ['Smart 265', 'H.265', 'H.264', 'MJPEG'],
      onvif: true,
      formFactor: '1.5U',
      alarmIO: '16 in / 4 out',
      networkPorts: '1× 10/100/1000 RJ-45',
    },
  },
];
